import { Stack, Typography } from "@mui/material";

import React, { useState } from "react";
import CaptionAndFilter from "./component/CaptionAndFilter";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import { useParams } from "react-router-dom";
import { useCustomQuery } from "store/hooks/react-query/query/useQuery";
import { VITAL_SIGNS_GRAPH } from "utils/reactQueryKeys";
import { useSanitizeQueryParams } from "utils/sanitizeQuery";
import CustomLoader from "components/atoms/CustomLoader";
import CustomGraphTooltip from "./component/CustomGraphTooltip";
import CustomRefLine from "./component/CustomRefLine";

const Graph = ({ data }) => {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data?.graph ?? []}>
        <defs>
          <linearGradient id="gradientFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="10%" stopColor="rgba(26, 147, 111, 0.4)" />
            <stop offset="100%" stopColor="rgba(26, 147, 111, 0)" />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey="xName" />
        <YAxis
          unit={data?.range?.unit}
          domain={["auto", Number(data?.range?.normal?.max || 0) + 5]}
          allowDecimals
          tickCount={10}
        />
        <Tooltip content={<CustomGraphTooltip yName="Saturation" />} />

        <ReferenceLine
          y={Number(data?.range?.normal?.max ?? 0)}
          label={
            <CustomRefLine
              range={`${data?.range?.normal?.min}${data?.range?.unit} - ${data?.range?.normal?.max}${data?.range?.unit}`}
            />
          }
          stroke="#00843566"
          strokeDasharray="5 5"
        />
        <Area
          type="monotone"
          dataKey="yValue"
          unit={data?.range?.unit}
          stroke="#1A936F"
          strokeWidth={"3px"}
          fill="url(#gradientFill)"
          dot={false}
        />
        <ReferenceLine
          y={Number(data?.range?.normal?.min ?? 0)}
          label=""
          stroke="#00843566"
          strokeDasharray="5 5"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

const VITALS_CHECKED = "oxygenSaturation";

function OxygenSaturation() {
  const { id } = useParams();

  const [filter, setfilter] = useState({
    name: "This Month",
    value: "ThisMonth",
  });

  //get vital signs
  const {
    data: vitalsGraph,
    isLoading,
    isError,
  } = useCustomQuery(
    [
      VITAL_SIGNS_GRAPH,
      id,
      {
        timeType: filter.value,
        vitalSign: VITALS_CHECKED,
      },
    ],
    {
      url: useSanitizeQueryParams(
        `/visit/patients/${id}/vitals-graph?timeType=${filter.value}&vitalSign=${VITALS_CHECKED}`
      ),
      method: "get",
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  const handleDropdown = (item) => {
    setfilter(item);
  };
  return (
    <Stack
      spacing={4}
      sx={{
        border: {
          lg: "1px solid rgba(196, 196, 196, 0.25)",
        },
        padding: {
          xs: 0,
          lg: 3,
        },
        borderRadius: {
          xs: "none",
          lg: "12px",
        },
      }}
    >
      <CaptionAndFilter
        filter={filter}
        handleDropdown={handleDropdown}
        icon={
          <svg
            width="28"
            height="27"
            viewBox="0 0 28 27"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13.4037 5.05664C7.36626 5.05664 2.45508 9.96695 2.45508 16.0052C2.45508 22.0427 7.36626 26.9547 13.4037 26.9547C19.4402 26.9547 24.3532 22.0418 24.3532 16.0052C24.3532 9.96782 19.4402 5.05664 13.4037 5.05664ZM13.4037 24.9881C8.44979 24.9881 4.4199 20.9583 4.4199 16.0026C4.4199 11.0496 8.44979 7.01972 13.4037 7.01972C18.3584 7.01972 22.3883 11.0505 22.3883 16.0026C22.3883 20.9583 18.3584 24.9881 13.4037 24.9881Z"
              fill="#1A936F"
            />
            <path
              d="M2.94723 9.01067C4.57494 9.01067 5.89446 7.69115 5.89446 6.06344C5.89446 4.43573 4.57494 3.11621 2.94723 3.11621C1.31952 3.11621 0 4.43573 0 6.06344C0 7.69115 1.31952 9.01067 2.94723 9.01067Z"
              fill="#1A936F"
            />
            <path
              d="M23.2416 7.62292C24.4861 7.62292 25.495 6.61406 25.495 5.36957C25.495 4.12507 24.4861 3.11621 23.2416 3.11621C21.9971 3.11621 20.9883 4.12507 20.9883 5.36957C20.9883 6.61406 21.9971 7.62292 23.2416 7.62292Z"
              fill="#1A936F"
            />
            <path
              d="M26.5879 2.8712C27.3679 2.8712 28.0001 2.23895 28.0001 1.45904C28.0001 0.679121 27.3679 0.046875 26.5879 0.046875C25.808 0.046875 25.1758 0.679121 25.1758 1.45904C25.1758 2.23895 25.808 2.8712 26.5879 2.8712Z"
              fill="#1A936F"
            />
            <path
              d="M8.47446 3.11689C9.08491 3.11689 9.57978 2.62202 9.57978 2.01157C9.57978 1.40112 9.08491 0.90625 8.47446 0.90625C7.86401 0.90625 7.36914 1.40112 7.36914 2.01157C7.36914 2.62202 7.86401 3.11689 8.47446 3.11689Z"
              fill="#1A936F"
            />
            <path
              d="M10.2522 11.376C8.88186 11.376 7.81315 11.7595 7.04605 12.5266C6.27808 13.2946 5.89453 14.365 5.89453 15.7441C5.89453 16.73 6.08805 17.5511 6.47596 18.2066C6.86299 18.8648 7.36858 19.3433 7.99272 19.6467C8.61599 19.9483 9.40401 20.1009 10.3559 20.1009C11.2921 20.1009 12.074 19.9256 12.7017 19.5744C13.3152 19.2351 13.8157 18.7232 14.1409 18.102C14.4721 17.4692 14.6386 16.662 14.6386 15.6743C14.6386 14.3154 14.2585 13.2589 13.4984 12.5057C12.7392 11.7517 11.6565 11.376 10.2522 11.376ZM11.5702 17.6034C11.2625 17.9678 10.8292 18.15 10.2696 18.15C9.72393 18.15 9.29418 17.9643 8.97775 17.5912C8.66045 17.2181 8.50267 16.6071 8.50267 15.7554C8.50267 14.8968 8.66219 14.2805 8.98124 13.9083C9.29941 13.5361 9.72131 13.3486 10.2478 13.3486C10.7953 13.3486 11.2302 13.5326 11.5519 13.8987C11.8718 14.2648 12.0331 14.8454 12.0331 15.6404C12.0313 16.5835 11.877 17.2391 11.5702 17.6034ZM17.51 18.4237C17.6155 18.3383 17.8238 18.1517 18.1376 17.9347C18.6632 17.5633 19.0259 17.2059 19.2255 16.8973C19.4234 16.587 19.5236 16.2532 19.5236 15.9141C19.5257 15.6029 19.4337 15.2984 19.2596 15.0404C19.0856 14.7825 18.8376 14.5832 18.5482 14.4688C18.2431 14.3433 17.8194 14.2796 17.2729 14.2796C16.749 14.2796 16.341 14.3441 16.0438 14.4758C15.7645 14.5915 15.5257 14.7875 15.3577 15.0389C15.1956 15.283 15.084 15.6238 15.023 16.0614L16.5773 16.1887C16.62 15.874 16.7045 15.6534 16.8309 15.5279C16.8951 15.4658 16.9713 15.4173 17.0547 15.3854C17.1381 15.3535 17.2272 15.3388 17.3165 15.3422C17.5074 15.3422 17.6651 15.4015 17.7915 15.5227C17.9179 15.6438 17.9798 15.7885 17.9798 15.9585C17.9798 16.1154 17.9162 16.2828 17.7898 16.4571C17.6617 16.6323 17.3714 16.8878 16.9164 17.2251C16.1719 17.7786 15.6655 18.1857 15.3952 18.5885C15.125 18.9947 14.9629 19.8158 14.9097 19.8158H19.1601V18.8334H17.1421C17.2807 18.8343 17.4045 18.5091 17.51 18.4237Z"
              fill="#1A936F"
            />
          </svg>
        }
        title={"Oxygen Saturation"}
        subTitle={"Oxygen level in the blood as a percentage."}
      />
      <Stack
        sx={{
          width: {
            xs: "70vw",
            sm: "50vw",
          },
          height: {
            xs: "35vh",
            sm: "40vh",
            md: "70vh",
          },
        }}
      >
        {isLoading ? (
          <CustomLoader />
        ) : !isError ? (
          <Graph data={vitalsGraph.data} />
        ) : (
          "No data to display"
        )}
        {!isLoading && !isError && (
          <Typography
            sx={{
              fontWeight: "bold",
              fontSize: "14px",
              opacity: 0.7,
              textAlign: "center",
            }}
          >
            {filter.name}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}

export default OxygenSaturation;
